import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DocumentService } from '../services/APIService';

// Async thunks for API calls
export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await DocumentService.getAllDocuments();
      // response is already { status, message, data: [...] }
      return response.data || [];
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const fetchDocumentById = createAsyncThunk(
  'documents/fetchDocumentById',
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await DocumentService.getDocument(documentId);
      // response is { status, message, data: {...} }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const createDocument = createAsyncThunk(
  'documents/createDocument',
  async (documentData, { rejectWithValue }) => {
    try {
      const response = await DocumentService.createDocument(documentData);
      // response is { status, message, data: {...} }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const updateDocument = createAsyncThunk(
  'documents/updateDocument',
  async ({ documentId, documentData }, { rejectWithValue }) => {
    try {
      const response = await DocumentService.updateDocument(documentId, documentData);
      // response is { status, message, data: {...} }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const deleteDocument = createAsyncThunk(
  'documents/deleteDocument',
  async (documentId, { rejectWithValue }) => {
    try {
      await DocumentService.deleteDocument(documentId);
      return documentId;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

const initialState = {
  items: [],
  currentDocument: null,
  loading: false,
  error: null,
  success: false,
};

const documentSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    resetCurrentDocument: (state) => {
      state.currentDocument = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Documents
    builder.addCase(fetchDocuments.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDocuments.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload;
      state.error = null;
    });
    builder.addCase(fetchDocuments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Document by ID
    builder.addCase(fetchDocumentById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDocumentById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentDocument = action.payload;
      state.error = null;
    });
    builder.addCase(fetchDocumentById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create Document
    builder.addCase(createDocument.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createDocument.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.items.push(action.payload);
      }
      state.success = true;
      state.error = null;
    });
    builder.addCase(createDocument.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update Document
    builder.addCase(updateDocument.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateDocument.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        const index = state.items.findIndex(d => d.id === action.payload.id);
        if (index > -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
      }
      state.success = true;
      state.error = null;
    });
    builder.addCase(updateDocument.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete Document
    builder.addCase(deleteDocument.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deleteDocument.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter(d => d.id !== action.payload);
      state.success = true;
      state.error = null;
    });
    builder.addCase(deleteDocument.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });
  },
});

export const { clearError, clearSuccess, resetCurrentDocument } = documentSlice.actions;
export default documentSlice.reducer;
